import React from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_learn-assessments.scss';
import '../styles/pages/_quiz-completion.scss';

interface ModuleSection {
  title: string;
  content: string[];
  illustrationText?: string;
  additionalSection?: {
    title: string;
    content: string[];
  };
  additionalSection2?: {
    title: string;
    content: string[];
  };
}

const moduleContent: ModuleSection[] = [
  {
    title: 'Assessing Your Environmental Quality of Life',
    content: [
      '<strong>Learning Goal:</strong> Identify personal strengths and challenges in environmental well-being, recognize which environmental areas need improvement, and connect satisfaction with surroundings to emotional and physical balance.',
      '<br/>',
      '<strong>Key Topics:</strong>',
      '• WHOQOL-BREF Environmental Domain assessment',
      '• Scoring and interpretation methods',
      '• Identifying areas for improvement',
      '• Connecting environmental satisfaction to overall well-being',
      '<br/>',
      '<strong>Discussion:</strong> Participants use the WHOQOL-BREF Environmental Domain (8 items) to evaluate their satisfaction with their surroundings. This assessment helps identify which environmental factors most impact your quality of life.'
    ]
  },
  {
    title: 'WHOQOL-BREF Environmental Domain',
    content: [
      'The World Health Organization Quality of Life assessment includes an Environmental Domain with 8 questions that measure satisfaction with your surroundings.',
      '<br/>',
      '<strong>The 8 Assessment Items:</strong>',
      '1. <strong>Safety:</strong> How safe do you feel in your daily life?',
      '2. <strong>Physical Environment:</strong> How satisfied are you with your physical environment (pollution, noise, traffic, climate)?',
      '3. <strong>Financial Resources:</strong> Do you have enough money to meet your needs?',
      '4. <strong>Health Services:</strong> How satisfied are you with the health services available to you?',
      '5. <strong>Transportation:</strong> How satisfied are you with your access to transportation?',
      '6. <strong>Living Conditions:</strong> How satisfied are you with your living conditions?',
      '7. <strong>Information Access:</strong> How satisfied are you with the information available to you?',
      '8. <strong>Leisure Opportunities:</strong> How satisfied are you with your opportunities for leisure activities?',
      '<br/>',
      '<strong>Rating Scale:</strong> Each item is rated from 1-5:',
      '• 1 = Very dissatisfied',
      '• 2 = Dissatisfied',
      '• 3 = Neither satisfied nor dissatisfied',
      '• 4 = Satisfied',
      '• 5 = Very satisfied'
    ]
  },
  {
    title: 'Scoring Your Environmental Assessment',
    content: [
      'Follow these steps to calculate and interpret your environmental well-being score.',
      '<br/>',
      '<strong>Step 1: Complete the Assessment</strong>',
      'Answer all 8 questions using the 1-5 scale based on your current life situation.',
      '<br/>',
      '<strong>Step 2: Calculate Raw Score</strong>',
      'Add up your scores from all 8 items.',
      'Raw score range: 8-40 points',
      '<br/>',
      '<strong>Step 3: Transform to 0-100 Scale</strong>',
      'Use this formula to convert your raw score to a standardized scale:',
      '<div style="background: #f5f5f5; padding: 15px; border-radius: 8px; margin: 15px 0; font-family: monospace; text-align: center;">',
      'Transformed Score = ((Raw Score - 8) / 32) × 100',
      '</div>',
      '<br/>',
      '<strong>Step 4: Interpret Results</strong>',
      `<div class="module-table">
        <div class="table-row header-row">
          <div class="table-header pillar-cell">SCORE RANGE</div>
          <div class="table-header means-cell">INTERPRETATION</div>
          <div class="table-header matters-cell">ACTION NEEDED</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">0-33</div>
          <div class="table-cell means-cell">Poor environmental well-being</div>
          <div class="table-cell matters-cell">Significant improvements needed</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">34-66</div>
          <div class="table-cell means-cell">Moderate environmental well-being</div>
          <div class="table-cell matters-cell">Some areas need attention</div>
        </div>
        <div class="table-row">
          <div class="table-cell pillar-cell">67-100</div>
          <div class="table-cell means-cell">Good environmental well-being</div>
          <div class="table-cell matters-cell">Maintain and optimize</div>
        </div>
      </div>`,
      '<br/>',
      '<strong>Example Calculation:</strong>',
      'If your raw score is 28: ((28 - 8) / 32) × 100 = (20 / 32) × 100 = 62.5',
      'Interpretation: Moderate environmental well-being - some areas need attention.'
    ]
  },
  {
    title: 'Analyzing Your Results',
    content: [
      'Your WHOQOL-BREF results provide valuable insights into your environmental well-being.',
      '<br/>',
      '<strong>Overall Score Analysis:</strong>',
      '• <strong>Low Scores (0-33):</strong> Multiple environmental factors are negatively impacting your quality of life. Focus on basic needs like safety and living conditions.',
      '• <strong>Moderate Scores (34-66):</strong> Some areas are working well, others need improvement. Prioritize the lowest-scoring domains.',
      '• <strong>High Scores (67-100):</strong> Your environment generally supports your well-being. Look for opportunities to optimize and maintain these positive conditions.',
      '<br/>',
      '<strong>Individual Item Review:</strong>',
      '• Identify your lowest-scoring items - these represent your biggest environmental challenges',
      '• Note your highest-scoring items - these are your environmental strengths',
      '• Look for patterns (e.g., multiple low scores in access-related items)',
      '<br/>',
      '<strong>Context Matters:</strong>',
      '• Consider your life circumstances (urban vs. rural, income level, etc.)',
      '• Think about what\'s within your control to change',
      '• Recognize that some factors may be influenced by broader systemic issues',
      '<br/>',
      '<strong>Emotional Connection:</strong> How does your environmental satisfaction affect your mood, stress levels, and overall life satisfaction? Low environmental quality often manifests as chronic stress or dissatisfaction.'
    ]
  },
  {
    title: 'Personal Environmental Profile Activity',
    content: [
      'Create your personal environmental well-being profile and action plan.',
      '<br/>',
      '<strong>Activity Steps:</strong>',
      '1. <strong>Take the Assessment:</strong> Answer the 8 WHOQOL-BREF environmental questions honestly',
      '2. <strong>Calculate Your Score:</strong> Use the formula to get your transformed score (0-100)',
      '3. <strong>Analyze Individual Items:</strong> Identify your highest and lowest scoring areas',
      '4. <strong>Reflect on Impact:</strong> Consider how these environmental factors affect your daily life',
      '<br/>',
      '<strong>Reflection Questions:</strong>',
      '• <strong>Which domain has the lowest score?</strong> What specific aspects of this area are problematic?',
      '• <strong>What\'s one realistic action</strong> you could take to raise satisfaction in your lowest-scoring area?',
      '• <strong>How do your environmental conditions</strong> affect your physical and mental health?',
      '• <strong>What environmental factors</strong> bring you the most joy or satisfaction?',
      '<br/>',
      '<strong>Action Planning:</strong>',
      'Choose your lowest-scoring domain and brainstorm 3 specific, achievable improvements.',
      'Example: If transportation scored low, actions might include: researching public transit options, carpooling with colleagues, or biking for short distances.',
      '<br/>',
      '<strong>Remember:</strong> Small, consistent improvements in your environment can lead to significant gains in overall well-being. Start with what\'s most feasible and impactful.'
    ]
  },
  {
    title: 'Ready to Test Your Knowledge?',
    content: [
      'You\'ve completed Module 3: Assessing Your Environmental Quality of Life! Now, let\'s reinforce your learning with a quick quiz.'
    ],
    additionalSection: {
      title: 'Next Steps',
      content: [
        'Click "Take the Quiz" below to test your knowledge about environmental assessment and quality of life evaluation.',
        'The quiz will help you review key concepts about measuring and improving your environmental well-being.'
      ]
    }
  }
];

const EnvironmentalModule3Page: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress, isCompleted } = useModuleProgress('environmentalM3');
  const totalSteps = moduleContent.length;

  const safeCurrentStep = Math.max(0, Math.min(currentStep, totalSteps - 1));
  const currentModule = moduleContent[safeCurrentStep];
  const progress = ((safeCurrentStep + 1) / totalSteps) * 100;

  const handleNext = async () => {
    if (currentStep < totalSteps - 1) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      await saveProgress(nextStep, false);
    } else {
      await saveProgress(currentStep, true);
      navigate('/panel/learn/environmental-resources');
    }
  };

  const handleBack = async () => {
    if (currentStep === 0) {
      navigate('/panel/learn/environmental-resources');
      return;
    }
    const prevStep = currentStep - 1;
    setCurrentStep(prevStep);
    await saveProgress(prevStep, isCompleted);
  };

  const showCompletionMessage = currentStep === totalSteps - 1;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  if (!currentModule) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Error loading module content. Please try refreshing the page.</p>
      </div>
    );
  }

  return (
    <div className="module-form">
      <div className="module-container">
        <div
          className="module-left environmental-panel"
        >
          <div className="module-header">
            <h1>Module 3</h1>
            <p className="module-subtitle">Assessing Your Environmental Quality of Life</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Use the WHOQOL-BREF Environmental Domain to evaluate your satisfaction with your surroundings.
            </p>
          </div>
        </div>

        <div className="module-right">
          <div className="module-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Page {currentStep + 1} of {totalSteps}
              </span>
            </div>

            <div className="module-section">
              <h2 className="module-title">
                {currentModule.title}
              </h2>
              <div className="content-section">
                {currentModule.content.map((paragraph, index) => (
                  <p key={index} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                ))}
              </div>
            </div>

            {currentModule.additionalSection && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection.content.map((paragraph: string, index: number) => (
                    <p key={`additional-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
            {currentModule.additionalSection2 && (
              <div className="module-section">
                <h2 className="module-title">{currentModule.additionalSection2.title}</h2>
                <div className="content-section">
                  {currentModule.additionalSection2.content.map((paragraph: string, index: number) => (
                    <p key={`additional2-${index}`} className="content-paragraph" dangerouslySetInnerHTML={{ __html: paragraph }} />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="navigation-buttons">
            <button
              className="btn-secondary"
              onClick={handleBack}
              disabled={false}
            >
              {currentStep === 0 ? 'Exit Module' : 'Previous'}
            </button>

            {showCompletionMessage ? (
              <button
                className="btn-primary"
                onClick={() => navigate('/panel/learn/environmental-module-3/quiz')}
              >
                Take the Quiz
              </button>
            ) : (
              <button
                className="btn-primary"
                onClick={handleNext}
              >
                Next
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnvironmentalModule3Page;

